/**
 * PORTED — RO-11-04 / #44589.
 * Origin: `excellent/app/_chrome/dock-bar-intake.tsx`.
 *
 * Changes: next/link → RouterLink; intake-capture constants local (not via
 * global-drop-layer, which stays on the Next tree until its own port).
 */

import * as React from "react";
import { RouterLink as Link } from "@excellent/ui/router";
import { useSWRConfig } from "swr";
import { Button, Icon, looksTabular, tabularShape } from "@excellent/ui";
import { apiCall, apiKey } from "@excellent/ui/api";
import { intake as intakeContract } from "@excellent/ui/api/contracts/intake";
import type { IntakeSnapshotDto } from "@excellent/ui/api/contracts/intake";
import { INTAKE_CAPTURE_EVENT, type IntakeCaptureDetail } from "./intake-capture";

/**
 * DockBar's ambient-capture (Universal Intake) concern — extracted from
 * `dock-bar.tsx` to keep that file within budget and read intake as one unit
 * (INTAKE-PRD §2.8.3/§2.8.4; mirrors the `dock-chat-transcript` split). Two
 * exports feed the dock:
 *   · `useDockIntake` owns the run/offer state + the paste / capture handlers
 *     and the global-drop-capture effect; the dock reads `intakeActive` for its
 *     panel gating and wires `onPaste` onto the input.
 *   · `IntakePanel` renders the dock's single inline outcome surface (the paste
 *     offer + "Paste as text" escape, the running line, the terminal outcome).
 *
 * The pure detectors (`looksTabular`/`tabularShape`) and the drop wiring
 * (`INTAKE_CAPTURE_EVENT`) stay in `@excellent/ui` / `global-drop-layer` — this
 * module holds only the dock-side glue and its render surface.
 */

// ─── intake hook (ambient capture) — INTAKE-PRD §2.8.3 ────────────────────────
// A pasted table / a bare URL / a globally-dropped file offer intake in the
// dock. The dock NEVER auto-consumes a paste (§2.8.3): a tabular paste surfaces
// an offer row with a "Paste as text" escape. Running intake shows the outcome
// inline in the panel — no toast, no second confirm UI (held bands route to
// Play). `focusInput`/`setQuery` are the dock's; everything else is owned here.

export type IntakeRun =
  | { phase: "running"; label: string }
  | { phase: "done"; snapshot: IntakeSnapshotDto }
  | { phase: "error"; message: string };

export type PasteOffer = { text: string; rows: number; columns: number };

export interface DockIntake {
  pasteOffer: PasteOffer | null;
  run: IntakeRun | null;
  /** Panel gating: an offer/run/outcome takes the dock panel over (§2.8.3). */
  active: boolean;
  /** Run intake (file/text/url) and announce the outcome inline. */
  runIntake: (body: { name: string; text: string; mode?: "text" | "url" }) => Promise<void>;
  /** Input paste handler — offers import for tabular text, never auto-consumes. */
  onPaste: (e: React.ClipboardEvent<HTMLInputElement>) => void;
  /** "Paste as text" escape — drop the offer, hand the raw text back to the bar. */
  pasteAsText: () => void;
  /** Clear any offer + outcome (the panel's Dismiss). */
  reset: () => void;
}

export function useDockIntake(opts: {
  focusInput: () => void;
  setQuery: (q: string) => void;
}): DockIntake {
  const { mutate } = useSWRConfig();

  const [pasteOffer, setPasteOffer] = React.useState<PasteOffer | null>(null);
  const [run, setRun] = React.useState<IntakeRun | null>(null);

  // The dock's `focusInput`/`setQuery` are re-created each render; hold them in a
  // ref so the handlers + capture effect stay stable (subscribe once, no churn).
  const dockRef = React.useRef(opts);
  dockRef.current = opts;

  // Run one intake through the shared contract (file/text/url) and announce the
  // outcome inline in the panel. Held bands (medium/low) say a confirm card is
  // waiting in Play (§2.8.3 parity copy — never "instant"); the /database ledger
  // revalidates so a batch shows up there.
  const runIntake = React.useCallback<DockIntake["runIntake"]>(
    async (body) => {
      setPasteOffer(null);
      dockRef.current.setQuery("");
      setRun({
        phase: "running",
        label: body.mode === "url" ? "Summarizing this page…" : "Landing rows and classifying…",
      });
      try {
        const snapshot = await apiCall(intakeContract.run, { body });
        setRun({ phase: "done", snapshot });
      } catch (e) {
        setRun({ phase: "error", message: e instanceof Error ? e.message : "Import failed" });
      } finally {
        mutate(apiKey(intakeContract.list, {}));
      }
    },
    [mutate],
  );

  // Paste — the CONSERVATIVE tabular detector OFFERS import; it never auto-consumes
  // (§2.8.3). Non-tabular pastes fall through to the input untouched.
  const onPaste = React.useCallback((e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData("text");
    if (!looksTabular(text)) return; // plain paste — do nothing (input takes it)
    const shape = tabularShape(text);
    e.preventDefault(); // hold the paste behind the offer + "Paste as text" escape
    setPasteOffer({ text, rows: shape?.rows ?? 0, columns: shape?.columns ?? 0 });
    dockRef.current.focusInput();
  }, []);

  // "Paste as text" escape (§2.8.3) — drop the offer and put the raw text in the bar.
  const pasteAsText = React.useCallback(() => {
    setPasteOffer((offer) => {
      if (offer) dockRef.current.setQuery(offer.text);
      return null;
    });
  }, []);

  const reset = React.useCallback(() => {
    setPasteOffer(null);
    setRun(null);
  }, []);

  // Intake capture: a file dropped ANYWHERE (the global drop layer) lands here
  // (INTAKE-PRD §2.8.3). Open the panel, run intake on the read text, host the
  // inline outcome — the dock is the single ambient-capture progress surface.
  React.useEffect(() => {
    function onCapture(e: Event) {
      const detail = (e as CustomEvent<IntakeCaptureDetail>).detail;
      if (!detail?.fileName) return;
      dockRef.current.focusInput();
      void runIntake({ name: detail.fileName, text: detail.text });
    }
    window.addEventListener(INTAKE_CAPTURE_EVENT, onCapture);
    return () => window.removeEventListener(INTAKE_CAPTURE_EVENT, onCapture);
  }, [runIntake]);

  // An active intake offer/run/outcome takes the dock panel over its own inline
  // surface (§2.8.3 — the dock hosts the ambient-capture outcome, no toast).
  const active = pasteOffer !== null || run !== null;

  return { pasteOffer, run, active, runIntake, onPaste, pasteAsText, reset };
}

// ─── intake panel (ambient capture outcome) ──────────────────────────────────────
// The dock's single ambient-capture surface (INTAKE-PRD §2.8.3/§2.8.4): the paste
// offer + "Paste as text" escape, the running line, and the terminal outcome —
// inline, no toast. Held bands (medium/low) announce that a confirm card is waiting
// in Play (the dock never grows a second confirm UI).

function intakeDrillHref(snap: IntakeSnapshotDto): string | null {
  if (snap.connectionId == null) return null;
  return `/settings/integrations/connections/${snap.connectionId}/records?model=intake-${snap.batchUid}`;
}

export function IntakePanel({
  offer,
  run,
  onImport,
  onPasteAsText,
  onDismiss,
}: {
  offer: PasteOffer | null;
  run: IntakeRun | null;
  onImport: () => void;
  onPasteAsText: () => void;
  onDismiss: () => void;
}) {
  // Offer (before any run) — a tabular paste was detected. Never auto-consumed.
  if (offer && !run) {
    return (
      <div className="flex flex-col gap-3 p-3">
        <div className="flex items-start gap-3">
          <span className="grid size-7 shrink-0 place-items-center rounded-md bg-primary/10 text-primary">
            <Icon name="table" className="size-3.5" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="type-body text-foreground">
              Pasted table · {offer.rows.toLocaleString()} {offer.rows === 1 ? "row" : "rows"} ×{" "}
              {offer.columns} {offer.columns === 1 ? "column" : "columns"}
            </p>
            <p className="type-subtext text-muted-foreground">Import it into your workspace, or paste it as text.</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" onClick={onImport}>
            <Icon name="download" className="size-3.5" /> Import as data
          </Button>
          <Button size="sm" variant="ghost" onClick={onPasteAsText}>
            Paste as text
          </Button>
        </div>
      </div>
    );
  }

  if (run?.phase === "running") {
    return (
      <div className="flex items-center gap-3 p-4">
        <Icon name="loader" className="size-4 animate-spin text-muted-foreground" />
        <span className="type-body text-muted-foreground">{run.label}</span>
      </div>
    );
  }

  if (run?.phase === "error") {
    return (
      <div className="flex flex-col gap-3 p-4">
        <p className="type-body text-foreground">Import failed — {run.message}</p>
        <div>
          <Button size="sm" variant="ghost" onClick={onDismiss}>
            Dismiss
          </Button>
        </div>
      </div>
    );
  }

  if (run?.phase === "done") {
    const snap = run.snapshot;
    const drill = intakeDrillHref(snap);
    const materialized = snap.status === "materialized";
    const held = snap.status === "awaiting_confirm" || snap.status === "awaiting_answer";
    const kind = snap.candidate?.kind ?? "records";
    return (
      <div className="flex flex-col gap-3 p-4">
        <div className="flex items-start gap-3">
          <span className="grid size-7 shrink-0 place-items-center rounded-md bg-muted text-muted-foreground">
            <Icon name={materialized ? "check" : "inbox"} className="size-3.5" />
          </span>
          <div className="min-w-0 flex-1">
            {materialized ? (
              <p className="type-body text-foreground">
                Imported {snap.materialized.toLocaleString()} {snap.materialized === 1 ? "row" : "rows"} as {kind}.
              </p>
            ) : held ? (
              <p className="type-body text-foreground">
                Landed {snap.landed.toLocaleString()} {snap.landed === 1 ? "row" : "rows"} — a confirm card is
                waiting in Play.
              </p>
            ) : (
              <p className="type-body text-foreground">
                Captured {snap.landed.toLocaleString()} {snap.landed === 1 ? "row" : "rows"} — kept as raw.
              </p>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {drill ? (
            <Button size="sm" variant="outline" asChild>
              <Link href={drill}>View records</Link>
            </Button>
          ) : (
            <Button size="sm" variant="outline" asChild>
              <Link href="/database">View in database</Link>
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={onDismiss}>
            Dismiss
          </Button>
        </div>
      </div>
    );
  }

  return null;
}
